'use client';

import type { Locale } from '../../../i18n/locales';
import { mapCarComparisonHref, resolvePublicHref } from '../../../lib/content/comparisonLinks';
import { parseWidgetItems } from '../../../lib/content/pageWidgets';
import type { PageWidget } from '../../../types/content';
import { FeatureCardsWidget } from './FeatureCardsWidget';
import { SplitHighlightWidget } from './SplitHighlightWidget';
import { MetricsStripWidget } from './MetricsStripWidget';
import { TestimonialCardsWidget } from './TestimonialCardsWidget';
import { ResourceListWidget } from './ResourceListWidget';
import { ContactCardsWidget } from './ContactCardsWidget';
import { CtaBannerWidget } from './CtaBannerWidget';

const toLocalizedHref = (
  locale: Locale,
  href?: string,
  options: { includeLegacyAutovakuutus?: boolean } = {},
): string | undefined => {
  if (!href?.trim()) return undefined;
  return resolvePublicHref(locale, href, options);
};

export function PageWidgetsRenderer({
  locale,
  widgets,
}: {
  locale: Locale;
  widgets: PageWidget[];
}) {
  if (!widgets.length) return null;

  const ordered = [...widgets].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <>
      {ordered.map((widget) => {
        if (!widget.isVisible) return null;

        const items = parseWidgetItems(widget.itemsJson);
        const mapHref = (href?: string) => toLocalizedHref(locale, href);

        if (widget.widgetType === 'feature_cards') {
          return (
            <FeatureCardsWidget
              key={String(widget.id)}
              badge={widget.badge}
              title={widget.title}
              subtitle={widget.subtitle}
              items={items}
              mapHref={mapHref}
            />
          );
        }

        if (widget.widgetType === 'split_highlight') {
          return (
            <SplitHighlightWidget
              key={String(widget.id)}
              badge={widget.badge}
              title={widget.title}
              body={widget.body || widget.subtitle}
              imageUrl={widget.imageUrl}
              imageAlt={widget.imageAlt}
              items={items}
              primaryCtaLabel={widget.primaryCtaLabel}
              primaryCtaHref={mapCarComparisonHref(widget.primaryCtaHref, { includeLegacyAutovakuutus: true })}
              secondaryCtaLabel={widget.secondaryCtaLabel}
              secondaryCtaHref={mapCarComparisonHref(widget.secondaryCtaHref)}
              mapHref={mapHref}
            />
          );
        }

        if (widget.widgetType === 'metrics_strip') {
          return (
            <MetricsStripWidget
              key={String(widget.id)}
              title={widget.title}
              subtitle={widget.subtitle || widget.body}
              items={items}
            />
          );
        }

        if (widget.widgetType === 'testimonial_cards') {
          return (
            <TestimonialCardsWidget
              key={String(widget.id)}
              title={widget.title}
              subtitle={widget.subtitle}
              items={items}
            />
          );
        }

        if (widget.widgetType === 'resource_list') {
          return (
            <ResourceListWidget
              key={String(widget.id)}
              title={widget.title}
              subtitle={widget.subtitle}
              items={items}
              mapHref={mapHref}
            />
          );
        }

        if (widget.widgetType === 'contact_cards') {
          return (
            <ContactCardsWidget
              key={String(widget.id)}
              title={widget.title}
              subtitle={widget.subtitle}
              items={items}
              mapHref={mapHref}
            />
          );
        }

        if (widget.widgetType === 'cta_banner') {
          return (
            <CtaBannerWidget
              key={String(widget.id)}
              title={widget.title}
              subtitle={widget.subtitle}
              primaryCtaLabel={widget.primaryCtaLabel}
              primaryCtaHref={mapCarComparisonHref(widget.primaryCtaHref, { includeLegacyAutovakuutus: true })}
              secondaryCtaLabel={widget.secondaryCtaLabel}
              secondaryCtaHref={mapCarComparisonHref(widget.secondaryCtaHref)}
              mapHref={mapHref}
            />
          );
        }

        return null;
      })}
    </>
  );
}
